import type { CatalogOption, Phase } from '../reserva/types'
import type { Money, StayRef } from '../types'

export type PaymentMethodKind = 'card' | 'saved_card' | 'apple_pay' | 'google_pay' | 'bizum'

export interface PaymentLine {
  kind: 'accommodation' | 'tourist_tax' | 'extra' | 'fee' | 'paid' | 'discount'
  concept: string
  amount: Money
}

export interface SavedCard {
  id: string
  last4: string | null
  holder: string | null
  expiry: string | null
  /** El PMS no guarda el CVV: hay que pedirlo */
  needsCvv: boolean
}

/** Resumen de pagos de la reserva (openapi: PaymentSummary). */
export interface PaymentSummary {
  total: Money
  pending: Money
  paid: Money
  /** Desglose por concepto: la API actual no lo devuelve (solo mock) */
  lines: PaymentLine[]
  /** Solo tarjeta y tarjeta guardada (inventario D10) */
  methods: { id: string; kind: PaymentMethodKind }[]
  savedCard: SavedCard | null
  /** Anticipo (parte del total que se cobra al reservar) */
  advance: { amount: Money | null; percent: number | null; paid: boolean; paidAt: string | null } | null
  /** Resto (lo que queda tras el anticipo) */
  rest: { amount: Money | null; paid: boolean; paidAt: string | null } | null
  /** Caso actual `cardChannel && !previousPaidOnCard`: hay que validar la tarjeta del canal antes de pagar */
  channelValidation: { required: boolean; channelName: string | null }
  freeCancel: boolean
  freeCancelUntil: string | null
  rateConditions: string | null
  /** Identificadores del PMS que necesita el pago (E3) */
  ids: { standard: string | null; advance: string | null; rest: string | null; bail: string | null; paymentCard: string | null; paymentBizum: string | null }
}

/**
 * Qué se paga. Decide el `paidTypeId` de E3 como la app actual: anticipo, resto (total pendiente), parcial
 * (`paidTypeStandardId`, tras un pago fallido), fianza, y validación de la tarjeta del canal (anticipo si
 * falta, si no el resto; ChannelValidation.tsx).
 */
export type PaymentKind = 'advance' | 'rest' | 'partial' | 'deposit' | 'channel'

/**
 * Tarjeta con la que se paga. Los datos de una tarjeta nueva viajan al PMS como hoy (CardForm.tsx), que
 * devuelve la URL del banco. `cvv: null` en la guardada = el PMS ya lo tiene (`emptyCVV: false`).
 */
export type CardInput = { kind: 'new'; name: string; number: string; expiry: string; cvv: string } | { kind: 'saved'; id: string; cvv: string | null }

export interface PaymentRequest {
  kind: PaymentKind
  amount: Money
  card: CardInput
}

/** Resultado de E3: cobrado sin más, o hay que autorizar en el banco (iframe) y esperar a E4. */
export type PaymentStart = { status: 'completed' } | { status: 'redirect'; iframeUrl: string; temporalPaidId: string }

/** Resultado de la espera: autorizado, rechazado (cancelado en el banco) o tiempo agotado (sin respuesta). */
export type PaymentOutcome = 'completed' | 'rejected' | 'timeout'

export interface WaitOptions {
  signal?: AbortSignal
  /** Límite de la espera (por defecto PAYMENT_WAIT_MS) */
  timeoutMs?: number
}

/** Tasa turística (openapi: TouristTax). Sin endpoint. */
export interface TouristTax {
  total: Money
  perPersonNight: Money
  persons: number
  nights: number
  exemptPersons: number
  /** Quién no la paga, en el idioma pedido (depende del municipio) */
  exemptions: string[]
}

/**
 * Versión del texto de consentimiento que ve el huésped (`v2.pagos.tarjeta.permitir` + `permitirTexto`). Se envía
 * con cada registro para saber qué aceptó; cambiarla cuando cambie el texto en cualquier idioma.
 */
export const IN_STAY_CONSENT_TEXT_VERSION = '2026-09-17'

/**
 * Consentimiento de cargos durante la estancia (D37). Solo cuenta si lo registra el backend: sin endpoint el
 * estado es `pending_confirmation` y la app no habilita cargos. Nunca se guarda solo en el dispositivo.
 */
export interface InStayConsent {
  status: 'granted' | 'revoked' | 'pending_confirmation'
  recordedAt: string | null
  textVersion: string | null
}

export interface InStayConsentInput {
  granted: boolean
  textVersion: string
  /** Texto exacto mostrado, en el idioma del huésped */
  text: string
  lang: string
  /** Huésped que da el consentimiento (titular del registro), si se conoce */
  guestId: string | null
  /** Tarjeta guardada a la que se refiere (`validCardId`) */
  cardId: string
}

export interface SavedCardInfo {
  card: SavedCard & { brand: string | null }
  consent: InStayConsent
}

export interface Charge {
  id: string
  concept: string
  date: string
  amount: Money
  receiptSent: boolean
}

export interface Receipt {
  id: string
  concept: string
  date: string
  amount: Money
  url: string | null
}

export type ClientType = 'individual' | 'business'

/** Datos fiscales con los campos que exige E15 (`generatebill_guestapp`), como RequestInvoice.tsx. */
export interface FiscalData {
  clientType: ClientType
  name: string
  surname: string
  identifierTypeId: string
  identifier: string
  address: string
  postalCode: string
  city: string
  countryId: string
  provinceId: string
  email: string
}

export interface InvoiceInfo {
  bills: { id: string; url: string }[]
  /** La factura se puede pedir: la estancia ha terminado (misma regla que `isReserveFinished`) */
  canRequest: boolean
  /** Fecha límite para dejar los datos fiscales antes de la emisión; null si el PMS no la da */
  fiscalDeadline: string | null
  /** Datos guardados en el dispositivo (D22) */
  saved: FiscalData | null
}

export type PagosStay = StayRef & { flags: { paidWithGateway: boolean; cardChannel: boolean; previousPaidOnCard: boolean }; channel: { name: string | null }; currency: string }
export type InvoiceStay = StayRef & { bills: { id: string; url: string }[]; flags: { showGetBills: boolean }; dates: { checkOut: string }; phase: Phase }

export interface PagosService {
  getPaymentSummary(stay: PagosStay, lang: string, signal?: AbortSignal): Promise<PaymentSummary>
  /** E2 (¿ya pagado?) → E3. Lanza `already_paid` o `payment_failed`. */
  pay(stay: PagosStay, request: PaymentRequest, lang: string, signal?: AbortSignal): Promise<PaymentStart>
  /** E4 cada segundo hasta completado, cancelado o el límite. Los fallos de red reintentan. */
  waitForResult(stay: StayRef, temporalPaidId: string, options?: WaitOptions): Promise<PaymentOutcome>
  /** Tarjeta guardada (E2) y consentimiento de cargos (dispositivo). null = no hay tarjeta guardada. */
  getSavedCard(stay: PagosStay, lang: string, signal?: AbortSignal): Promise<SavedCardInfo | null>
  /** Registra el consentimiento en el backend. Sin endpoint: `not_supported` (D37). */
  setInStayConsent(stay: StayRef, input: InStayConsentInput): Promise<InStayConsent>
  /** Cargos durante la estancia. Sin endpoint. */
  getCharges(stay: StayRef, signal?: AbortSignal): Promise<Charge[]>
  /** null = la reserva no tiene tasa. Sin endpoint. */
  getTouristTax(stay: StayRef, signal?: AbortSignal): Promise<TouristTax | null>
  /** "¿Alguien de la reserva está exento? Díselo al hotel". Sin endpoint. */
  notifyTaxExemption(stay: StayRef, text: string): Promise<{ sentAt: string }>
  /** Recibo por pago. Sin endpoint. */
  getReceipts(stay: StayRef, signal?: AbortSignal): Promise<Receipt[]>
  resendReceipts(stay: StayRef, email: string): Promise<{ sentTo: string }>
  getInvoiceInfo(stay: InvoiceStay, signal?: AbortSignal): Promise<InvoiceInfo>
  /** Guarda los datos fiscales antes de la emisión (dispositivo, D22). */
  saveFiscalData(stay: StayRef, data: FiscalData): Promise<void>
  /** E15: genera la factura con los datos fiscales. */
  requestInvoice(stay: StayRef, data: FiscalData): Promise<{ id: string; url: string }[]>
  /** E7: provincias de un país (para la dirección fiscal). */
  getProvinces(stay: StayRef, countryId: string, signal?: AbortSignal): Promise<CatalogOption[]>
}
